// ===========================================
// Login Page - with Google OAuth
// ===========================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Zap, Mail, Lock, ArrowRight, Loader } from 'lucide-react';
import './Auth.scss';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Remember me checkbox - defaults to checked
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Ref for Google Sign-In button container
  const googleButtonRef = useRef(null);
  
  const { login, googleLogin, isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ===========================================
  // Handle Google OAuth Response
  // ===========================================
  // Callback receives credential (ID token) from Google
  const handleGoogleResponse = useCallback(async (response) => {
    setFormError('');
    setIsSubmitting(true);
    
    const result = await googleLogin(response.credential, rememberMe);
    
    if (!result.success) {
      setFormError(result.error);
    }
    
    setIsSubmitting(false);
  }, [googleLogin, rememberMe]);

  // ===========================================
  // Initialize Google Sign-In Button
  // ===========================================
  useEffect(() => {
    // Wait for Google Identity Services script to load
    const initializeGoogleSignIn = () => {
      if (window.google && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse
        });
        
        // Render the Google Sign-In button
        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          { 
            theme: 'filled_black',    // Dark theme to match DevPad aesthetic
            size: 'large',
            width: 320,               // Fixed width for consistency
            text: 'continue_with',    // "Continue with Google"
            shape: 'rectangular'
          }
        );
      }
    };

    // Check if script is already loaded, otherwise wait for it
    if (window.google) {
      initializeGoogleSignIn();
    } else {
      // Script loads async, so we poll for it
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogleSignIn();
        }
      }, 100);
      
      // Clean up interval on unmount
      return () => clearInterval(checkGoogle);
    }
  }, [handleGoogleResponse]);

  // Redirect if already authenticated - go to dashboard or intended page
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear error on unmount
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Basic validation
    if (!email || !password) {
      setFormError('Please fill in all fields');
      return;
    }
    
    setIsSubmitting(true);
    
    // Pass rememberMe to determine storage type (localStorage vs sessionStorage)
    const result = await login(email, password, rememberMe);
    
    if (!result.success) {
      setFormError(result.error);
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="auth-page">
      {/* Background decoration */}
      <div className="auth-page__bg">
        <div className="auth-page__gradient"></div>
        <div className="auth-page__grid"></div>
      </div>

      <div className="auth-container">
        {/* Logo */}
        <div className="auth-logo">
          <div className="auth-logo__icon">
            <Zap size={28} />
          </div>
          <div className="auth-logo__text">
            <span className="auth-logo__title">DevPad</span>
            <span className="auth-logo__subtitle">Developer Headquarters</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="auth-card">
          <div className="auth-card__header">
            <h1>Welcome back</h1>
            <p>Sign in to continue building amazing projects</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {/* Error Message */}
            {(formError || error) && (
              <div className="auth-form__error">
                {formError || error}
              </div>
            )}

            {/* Email Field */}
            <div className="auth-form__group">
              <label htmlFor="email">Email</label>
              <div className="auth-form__input-wrapper">
                <Mail size={18} className="auth-form__input-icon" />
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="auth-form__group">
              <label htmlFor="password">Password</label>
              <div className="auth-form__input-wrapper">
                <Lock size={18} className="auth-form__input-icon" />
                <input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="auth-form__remember">
              <label className="auth-form__checkbox">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="auth-form__checkbox-mark"></span>
                <span className="auth-form__checkbox-label">Remember me</span>
              </label>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="auth-form__submit btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight size={18} />
                </>
              )}
            </button>

            {/* Divider */}
            <div className="auth-form__divider">
              <span>or</span>
            </div>

            {/* Google Sign-In Button */}
            <div 
              ref={googleButtonRef} 
              className="auth-form__google-btn"
            ></div>
          </form>

          {/* Footer Link */}
          <div className="auth-card__footer">
            <p>
              Don't have an account?{' '}
              <Link to="/register">Create one</Link>
            </p>
          </div>
        </div>

        {/* Bottom Text */}
        <p className="auth-footer">
          Build, store, and display your projects with precision context and AI assistance.
        </p>
      </div>
    </div>
  );
};

export default Login;

